import {
  Button,
  cssVar,
  makeStyles,
  TextField,
} from '@amagimedia/amagi-design-system';
import { GlobalLine } from '@amagimedia/amagi-design-system/src/icons/GlobalLine';
import { Typography } from '@amagimedia/amagi-design-system/src/Typography/Typography';
import { signInStvV2 } from '@amagimedia/commons/src/apis/auth-stv-v2';
import { getBaseDomain } from '@amagimedia/commons/src/utils';
import { useCallback, useEffect, useState, type FC } from 'react';
import { domainNamePattern } from '../constants/regex';
import { HomeScreenContainer } from './HomeScreenContainer';

const useStyles = makeStyles({
  button: {
    padding: '8px 16px',
    marginTop: '37px',
  },
  mandatorySymbol: {
    color: cssVar('--primary-100'),
  },
  domainName: {
    color: cssVar('--neutrals-grey-300'),
    marginBottom: '8px',
  },
  startAdornment: {
    marginRight: '6px',
  },
  endAdornment: {
    color: cssVar('--neutrals-grey-200'),
  },
});

interface ILocalState {
  domain: string;
  isValid: boolean;
  submitting: boolean;
}

function getDomain() {
  const host = window.location.host;
  const baseDomain = getBaseDomain();
  let domain = '';
  if (host.includes(baseDomain)) {
    const splittedDomain = host.split('.');
    const envTypeIndex = splittedDomain.findIndex(
      (e) => e === process.env.ENV_TYPE,
    );
    if (envTypeIndex === 1 || splittedDomain.length === 4) {
      domain = splittedDomain[0];
    }
  }
  return domain;
}

export const SignIn: FC = () => {
  const classes = useStyles();
  const [localState, setLocalState] = useState<ILocalState>({
    domain: '',
    isValid: true,
    submitting: false,
  });

  const handleSignin = useCallback((domain: string) => {
    //using setLocalState directly instead of updateState function to avoid dependency issues and multiple rerenders
    setLocalState((state) => ({ ...state, submitting: true }));
    signInStvV2(`${domain}.${getBaseDomain()}`);
  }, []);

  useEffect(() => {
    const domain = getDomain();
    if (domain) {
      //using setLocalState directly instead of updateState function to avoid dependency issues
      setLocalState((state) => ({ ...state, domain }));
      handleSignin(domain);
    }
  }, [handleSignin]);

  function updateState(updatedState: ILocalState) {
    setLocalState(updatedState);
  }

  function handleValueChange(value: string) {
    updateState({
      ...localState,
      domain: value,
      isValid: domainNamePattern.test(value),
    });
  }

  function renderLabel() {
    return (
      <Typography type="bodyBoldDefault" className={classes.domainName}>
        Domain name <span className={classes.mandatorySymbol}>*</span>
      </Typography>
    );
  }

  return (
    <HomeScreenContainer>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSignin(localState.domain);
        }}
      >
        <TextField
          label={renderLabel()}
          autoFocus
          id="domainName"
          placeholder="Enter domain name"
          errorMessage={
            localState.domain === ''
              ? 'Domain name is required'
              : !localState.isValid
              ? 'Invalid Domain name format'
              : ''
          }
          value={localState.domain}
          onChange={(e) => handleValueChange(e.target.value)}
          fullWidth
          startAdornment={<GlobalLine className={classes.startAdornment} />}
          endAdornment={
            <Typography className={classes.endAdornment}>
              {`.${getBaseDomain()}`}
            </Typography>
          }
          data-testid="domain-name"
        />
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={!localState.domain}
          className={classes.button}
          data-testid="sign-in-stv-landing"
          loading={localState.submitting}
        >
          <Typography type="bodyBoldLarge">Sign in</Typography>
        </Button>
      </form>
    </HomeScreenContainer>
  );
};
